import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../../../lib/supabase'
import { useAuth } from '../../../hooks/useAuth'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

async function callEdgeFunction(name, body, session) {
  const res = await fetch(`${SUPABASE_URL}/functions/v1/${name}`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${session?.access_token}`,
    },
    body: JSON.stringify(body),
  })
  const json = await res.json()
  if (!res.ok) throw new Error(json.error ?? 'Request failed')
  return json
}

export const useLinkedHouseholds = () => {
  const { household, session } = useAuth()
  const qc  = useQueryClient()
  const key = ['linked-households', household?.id]

  const query = useQuery({
    queryKey: key,
    enabled:  !!household?.id,
    queryFn:  async () => {
      // Fetch links where this household is primary or linked
      const { data, error } = await supabase
        .from('linked_households')
        .select(`
          id, status, link_code, expires_at, created_at, accepted_at,
          linked_by,
          primary_household_id,
          linked_household_id,
          primary:households!linked_households_primary_household_id_fkey(id, name),
          linked:households!linked_households_linked_household_id_fkey(id, name)
        `)
        .or(`primary_household_id.eq.${household.id},linked_household_id.eq.${household.id}`)
        .neq('status', 'revoked')
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []).map((link) => {
        const isPrimary = link.primary_household_id === household.id
        const otherHousehold = isPrimary ? link.linked : link.primary
        return {
          ...link,
          isPrimary,
          otherHousehold: otherHousehold ?? null,
        }
      })
    },
  })

  const inv = () => qc.invalidateQueries({ queryKey: key })

  const generateCode = async () => {
    const result = await callEdgeFunction('generate-link-code', { household_id: household.id }, session)
    inv()
    return result
  }

  const acceptCode = async (linkCode) => {
    const result = await callEdgeFunction('accept-link-code', { link_code: linkCode, household_id: household.id }, session)
    inv()
    qc.invalidateQueries({ queryKey: ['calendars', household?.id] })
    qc.invalidateQueries({ queryKey: ['members', household?.id] })
    return result
  }

  const revokeLink = async (linkId) => {
    await callEdgeFunction('revoke-link', { link_id: linkId }, session)
    inv()
    qc.invalidateQueries({ queryKey: ['calendars', household?.id] })
    qc.invalidateQueries({ queryKey: ['events', household?.id] })
    qc.invalidateQueries({ queryKey: ['members', household?.id] })
    qc.invalidateQueries({ queryKey: ['shared-calendars', household?.id] })
    qc.invalidateQueries({ queryKey: ['shared-members', household?.id] })
  }

  // The single active link for this household (as primary or linked)
  const activeLink = (query.data ?? []).find((l) => l.status === 'active') ?? null
  const pendingLink = (query.data ?? []).find((l) => l.status === 'pending') ?? null

  return { ...query, generateCode, acceptCode, revokeLink, activeLink, pendingLink }
}
